import { createColorStack, finalizeColorStack } from './colorStack';
import { standardLabels, standardDefinitions } from './standardColors';
import { createAccessoryPaletteFromSeed } from './colorFromSeed';
import chroma from 'chroma-js';

/**
 * createColorPalette is used to build custom Carina themes. You'll need a finalizedColorPalette and a formula to create tokens.
 * The GUI palette builder (Carina docsite) produces a complexColorPalette. The JSON output by the palette builder can be used to produce tokens. complexColorPalettes produced with createColorPalette function are also compatible with the GUI on the docsite.
 * complexColorPalettes can be serialized with JSON.stringify().
 * After creating a complexColorPalette, remember to use finalizeColorPalette() before creating tokens with a formula.

 * @param {...(object|array)} complexColorStack createColorPalette accepts complexColorStack objects as parameters. A parameter can also be an array of values (such as standardDefinitions.accent) that createColorPalette will use to produce a complexColorStack(see ColorStack API).

 * @example 
  const complexColorPalette = createColorPalette(
    createColorStack('#006ae1', 'interactive', true), 
    standardDefinitions.brand, 
    ['#00ca9d', 'accent']
  );

 * @returns {array} complexColorPalette (an array of complexColorStack objects). Carina palettes must always have a stack for every one of the standardLabels: neutral, brand, interactive, accent, tertiary, danger, warning, success'. If no stack for a given label is provided as an argument, the standardConstructor will be used for that label. For example, if no arguments are provided when instantiating a ColorPalette, the return value will be the standard Carina palette.
 */
export function createColorPalette(...stacksAndConstructors) {
  const stacks = stacksAndConstructors.reduce((acc, stackOrConstructor) => {
    if (Array.isArray(stackOrConstructor)) {
      /* if the stack is a seed color, generate accessory palette */
      if (
        stackOrConstructor[1] === 'seed' &&
        chroma.valid(stackOrConstructor[0])
      ) {
        return [
          ...acc,
          ...createAccessoryPaletteFromSeed(stackOrConstructor[0]).colorStacks
        ];
      }
      /* Create new color stack from array */
      return [...acc, createColorStack(...stackOrConstructor)];
    }
    return [...acc, stackOrConstructor];
  }, []);

  const presentStackLabels = stacks.reduce(
    (accumulator, stack) =>
      stack.label ? accumulator.concat(stack.label) : accumulator,
    []
  );

  /* Add standard stacks if not included as arguments (determined by label) */
  const stacksWithStandardStacks = [...stacks];
  Object.values(standardLabels).forEach(
    label =>
      presentStackLabels.includes(label) ||
      stacksWithStandardStacks.push(
        createColorStack(...standardDefinitions[label])
      )
  );

  return stacksWithStandardStacks;
}

export default createColorPalette;

/**
 * @function finalizeColorPalette converts a complexColorPalette (the return value of createColorStack()) to an object that can be used by a Carina formula to produce tokens. After a complexColorPallet has been finalized its values are clearer and easier to access, but it can no longer be used in the palette builder (Carina docsite), and only final values will be kept. Modifying a finalized ColorPalette instance is not recommended. Example use: finalizeColorPalette(createColorPalette())
 */
export function finalizeColorPalette(palette) {
  const simplifiedDefault = {};
  const simplifiedDark = {};

  palette.forEach(stack => {
    /* reduce stack with more than one label */
    if (Array.isArray(stack.label)) {
      stack.label.forEach(label => {
        const { default: defaultStack, dark: darkStack } = finalizeColorStack({
          ...stack,
          label
        });
        simplifiedDefault[label] = defaultStack;
        simplifiedDark[label] = darkStack;
      });
    } else {
      /* reduce stack with only one label */
      const { default: defaultStack, dark: darkStack } = finalizeColorStack(
        stack
      );
      simplifiedDefault[stack.label] = defaultStack;
      simplifiedDark[stack.label] = darkStack;
    }
  });
  return { default: simplifiedDefault, dark: simplifiedDark };
}
